import { FeatureFlagBool, useFeatureFlagBoolList } from '@modernloop/shared/feature-flag';

import { AtsType } from 'src/generated/mloop-graphql';

import { useIntegrations } from '../api/integration';

import AshbyAtsService from './AshbyAtsService';
import AtsSevice from './AtsService';
import GemAtsService from './GemAtsService';
import GreenhouseAtsService from './GreenhouseAtsService';
import KulaAtsService from './KulaAtsService';
import LeverAtsService from './LeverAtsService';
import SmartRecruitersAtsService from './SmartRecruitersAtsService';
import WorkdayAtsService from './WorkdayAtsService';

/* *
 * @description
 * Fetches the ATS type from the integrations, new ats to be added here
 * FE should be completely agnostic to the ATS type
 * This hook should be used strictly within this file and never be used directly
 * Infact even on ATS service, we should not have any ats type stored
 * IMP: Never export
 * */

const useCurrentATSType = (): AtsType | null => {
  const { data } = useIntegrations();

  if (!data) {
    return null;
  }

  if (data.greenhouse_oauth) {
    return AtsType.Greenhouse;
  }

  if (data.lever) {
    return AtsType.Lever;
  }

  if (data.ashby) {
    return AtsType.Ashby;
  }

  if (data.gem) {
    return AtsType.Gem;
  }

  if (data.workday) {
    return AtsType.Workday;
  }

  if (data.smart_recruiters) {
    return AtsType.SmartRecruiters;
  }

  if (data.kombo) {
    return AtsType.Kula;
  }

  return null;
};

/**
 * @param atsType
 * @returns boolean
 * @description
 * Returns true if the org ATS is the same as the atsType passed
 * Currently only used at 2 place
 * Note: If used too many places, consider moving this in AtsSevice
 */
const useIsOnCurrentAts = (atsType?: AtsType): boolean => {
  const ats = useCurrentATSType();
  return atsType === ats;
};

/* *
 * @param atsType
 * @returns AtsSevice
 * @description Returns the ATS service based on the ATS type.
 * To be used when atsType is know from an entity eg Application, Candidate or Job
 * If not always use useOrgAtsService
 * */
const getAtsService = (atsType: AtsType): AtsSevice => {
  switch (atsType) {
    case AtsType.Greenhouse:
      return GreenhouseAtsService;
    case AtsType.Lever:
      return LeverAtsService;
    case AtsType.Ashby:
      return AshbyAtsService;
    case AtsType.Gem:
      return GemAtsService;
    case AtsType.Workday:
      return WorkdayAtsService;
    case AtsType.SmartRecruiters:
      return SmartRecruitersAtsService;
    case AtsType.Kula:
      return KulaAtsService;
    default:
      throw new Error(`Unsupported ATS type: ${atsType}`);
  }
};

/* *
 * @description
 * Returns the ATS service based on the org ATS type
 * */
const useOrgAtsService = () => {
  const ats = useCurrentATSType();
  if (!ats) {
    return null;
  }
  return getAtsService(ats);
};

/**
 * @description
 * Returns all the ATS services instance integrated with Mloop
 * @returns AtsService[]
 * Added in the sequence of ATS added in the Mloop
 * Note: If a new ATS is added, add it here, don't export this function
 */
const getAllAtsServices = (): AtsSevice[] => {
  return [
    GreenhouseAtsService,
    LeverAtsService,
    AshbyAtsService,
    GemAtsService,
    WorkdayAtsService,
    SmartRecruitersAtsService,
    KulaAtsService,
  ];
};

/**
 * @description
 * Returns all the ATS services integrated with Mloop
 * @returns AtsService[]
 * Note: Don't export this function
 */
const getEligibleAtsServices = (flagResults: Record<FeatureFlagBool, boolean>): AtsSevice[] => {
  const allAtsServices = getAllAtsServices();
  return allAtsServices.filter((ats) => {
    if (!ats.featureFlag) {
      return true; // if no feature flag is set, then this ATS is fully launched
    }
    // if feature flag set, then check if its launched
    return flagResults[ats.featureFlag];
  });
};

/**
 * @description
 * Returns all the ATS services integrated with Mloop and are eligible to be shown FF wise
 * @returns AtsService[]
 */
export const useEligibleAtsServices = () => {
  const flagResults = useFeatureFlagBoolList();
  return getEligibleAtsServices(flagResults);
};

export { useOrgAtsService, getAtsService, useIsOnCurrentAts };
